import {FilterCategory, FilterSets} from "@/components/filterDropdown/FilterDropdown.ts";
import {DEFAULT_SEARCH_KEY} from "@/lib/hooks/useDebouncedSearch.ts";
import {DEFAULT_FILTERS_KEY} from "@/lib/hooks/useFilterSets.ts";
import {DEFAULT_OFFSET_KEY} from "@/lib/hooks/usePagination.ts";
import {MonitoringServiceType} from "@/lib/types/checks.ts";
import {QUERY} from "@/pages/checks/constants.ts";

const LEGACY_SEARCH_KEY = "s";
const LEGACY_STATUS_FILTER_KEY = "state";
const LEGACY_TAGS_FILTER_KEY = "tags";
const LEGACY_KEYS = [
  LEGACY_SEARCH_KEY,
  QUERY.MONITORING_SERVICE_TYPE,
  LEGACY_STATUS_FILTER_KEY,
  LEGACY_TAGS_FILTER_KEY,
];

/** Where a legacy `?pop=` Checks link points: a Check's editor or the create page. Undefined when it points nowhere. */
export const getLegacyPopRedirectPath = (pop: string): string | undefined => {
  if (parseInt(pop)) {
    return `/checks/${pop}/edit`;
  }
  if (["true", "initial"].includes(pop)) {
    return "/checks/create";
  }
  if (Object.values(MonitoringServiceType).includes(pop.toUpperCase() as MonitoringServiceType)) {
    return `/checks/create?${QUERY.MONITORING_SERVICE_TYPE}=${pop}`;
  }
  return undefined;
};

/**
 * Rewrites pre-SPA Checks list links (`?state=down&tags=a,b&s=api`, Django templates) into the keys
 * `useTable` reads. Returns undefined when the link has nothing to rewrite.
 */
export const normalizeLegacyChecksListParams = (params: URLSearchParams): URLSearchParams | undefined => {
  if (!LEGACY_KEYS.some((key) => params.has(key))) {
    return undefined;
  }
  const normalized = new URLSearchParams(params);
  LEGACY_KEYS.forEach((key) => {
    normalized.delete(key);
  });

  const legacySearch = params.get(LEGACY_SEARCH_KEY);
  if (legacySearch && !params.get(DEFAULT_SEARCH_KEY)) {
    normalized.set(DEFAULT_SEARCH_KEY, legacySearch);
  }

  if (params.has(DEFAULT_FILTERS_KEY)) {
    return normalized;
  }
  const legacyFilters: FilterSets<FilterCategory> = {};
  const monitoringServiceType = (params.get(QUERY.MONITORING_SERVICE_TYPE) || "").toUpperCase();
  if (Object.values(MonitoringServiceType).includes(monitoringServiceType as MonitoringServiceType)) {
    legacyFilters.monitoring_service_type = [monitoringServiceType];
  }
  const legacyStatus = params.get(LEGACY_STATUS_FILTER_KEY);
  if (legacyStatus) {
    legacyFilters.status = legacyStatus.split(",");
  }
  const legacyTags = params.get(LEGACY_TAGS_FILTER_KEY);
  if (legacyTags) {
    legacyFilters.tag_names = legacyTags.split(",");
  }
  if (Object.keys(legacyFilters).length > 0) {
    normalized.set(DEFAULT_FILTERS_KEY, JSON.stringify(legacyFilters));
    normalized.delete(DEFAULT_OFFSET_KEY);
  }
  return normalized;
};
